import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeammateAvatar} from '../../common/avatars/teammateAvatar';

/*
 * Props.
 */

interface CustomAttributeTeammateProps {
  teammateEmail: string;
  teammate: TeammateModel | undefined;
  isCompact?: boolean;
  shouldAllowOverflow?: boolean;
}

/*
 * Style.
 */

interface StyledContentDivProps {
  $shouldAllowOverflow?: boolean;
}

const StyledContentDiv = styled.div<StyledContentDivProps>`
  display: flex;
  line-height: 16px;
  overflow: hidden;

  ${(p) =>
    p.$shouldAllowOverflow &&
    css`
      overflow: visible;
    `}
`;

interface StyledTeammateAvatarProps {
  $isCompact?: boolean;
}
const StyledTeammateAvatar = styled(TeammateAvatar)<StyledTeammateAvatarProps>`
  margin-right: 8px;

  ${(p) =>
    p.$isCompact &&
    css`
      margin-right: 6px;
    `}
`;

interface StyledNameDivProps {
  $shouldAllowOverflow?: boolean;
}

const StyledNameDiv = styled.div<StyledNameDivProps>`
  flex: 1;

  ${(p) => !p.$shouldAllowOverflow && `${ellipsis()}`}
`;

/*
 * Component.
 */

export const CustomAttributeTeammate: FC<CustomAttributeTeammateProps> = (props) => {
  const {teammateEmail, teammate, isCompact, shouldAllowOverflow} = props;
  if (!teammate) {
    return <StyledContentDiv>{teammateEmail}</StyledContentDiv>;
  }

  const nameToRender = buildTeammateDisplayName(teammate);
  return (
    <StyledContentDiv $shouldAllowOverflow={shouldAllowOverflow}>
      <StyledTeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} $isCompact={isCompact} />
      <StyledNameDiv $shouldAllowOverflow={shouldAllowOverflow}>
        {renderName(nameToRender, Boolean(shouldAllowOverflow))}
      </StyledNameDiv>
    </StyledContentDiv>
  );
};

/*
 * Helpers.
 */

function renderName(name: string, shouldAllowOverflow: boolean) {
  if (shouldAllowOverflow) {
    return name;
  }
  return (
    <TooltipCoordinator render={() => name}>
      <TooltipOverflowCondition>{name}</TooltipOverflowCondition>
    </TooltipCoordinator>
  );
}
